/**
 * DEL LEVANTAMIENTO A LA COTIZACIÓN — el último eslabón.
 * =============================================================================
 *
 * Hasta el 2026-08-14 los levantamientos guiados eran islas. Riego trazaba el jardín,
 * elegía boquillas, dimensionaba tubería y bomba, y sacaba una lista de material
 * impecable **que no llegaba a ningún documento**. El eléctrico igual. El botón «Agregar
 * a la cotización» existía en el código y nunca se conectaba.
 *
 * Este archivo convierte el resultado de un levantamiento en algo que el motor de precios
 * ya sabe cobrar.
 *
 * ---------------------------------------------------------------------------
 * CÓMO SE ENGANCHA, Y POR QUÉ ASÍ
 *
 * La cotización se arma con renglones `{ sku, q, e1/e2/e3 }` que el motor resuelve contra
 * el catálogo. Ya existía un mecanismo para conceptos que **no** están en el catálogo
 * permanente: el renglón lleva un campo `def` con la definición del producto adentro, y
 * `App.jsx` la inyecta al contexto. Vive dentro de la cotización y sobrevive a recargar.
 *
 * Un levantamiento encaja ahí exactamente: es un producto irrepetible —el jardín de ESA
 * casa— cuyos componentes son el material que salió del plano. No tiene por qué ensuciar
 * el catálogo permanente, y no lo hace.
 *
 * ---------------------------------------------------------------------------
 * LO QUE SE CONSERVA, Y POR QUÉ IMPORTA
 *
 * El `def` no se queda solo con los números: guarda el **diseño completo** en `disenio`.
 * Sin eso, seis meses después nadie puede contestar por qué esa cotización pedía bomba de
 * 0.85 HP, y la promesa del Principio 9 —toda decisión automática es auditable— se
 * rompería justo donde más se necesita: frente a un cliente que reclama.
 * ============================================================================= */

/**
 * RENDIMIENTOS DE OBRA — cuántos minutos cuesta cada cosa.
 *
 * ⚠️ Éstos **sí** son datos de Juan y de nadie más. No hay norma ni ficha de fabricante
 * que diga cuánto tarda SU cuadrilla en abrir un metro de zanja: depende de su gente, sus
 * herramientas y el suelo de la zona. Por eso van marcados `sinVerificar` y viven en las
 * preferencias, editables desde Configuración.
 *
 * Los valores de arranque son órdenes de magnitud razonables para tener de dónde partir.
 * **Se calibran cronometrando la primera obra real**, y esa medición vale más que
 * cualquier estimación que se pueda hacer desde aquí.
 */
export const RENDIMIENTOS_BASE = {
  sinVerificar: true,

  // --- Riego ---
  /** Minutos por metro de zanja: abrir, tender el tubo y tapar. */
  minutoZanja: 8,
  /** Minutos por aspersor: cuerpo, conexión, nivelación y ajuste de arco. */
  minutoAspersor: 18,
  /** Minutos por válvula de zona, incluyendo su caja. */
  minutoValvula: 45,
  /** Minutos de instalar y programar el controlador. */
  minutoProgramador: 60,
  /** Minutos de montar la bomba: base, succión, descarga y cebado. */
  minutoBomba: 120,

  // --- Eléctrico ---
  /** Minutos por metro de canalización: ranura, tubo, resane. Es lo más caro del rubro. */
  minutoCanalizacion: 22,
  /** Minutos por circuito: tendido, conexión en tablero y en salida, pruebas. */
  minutoCircuito: 55,
  /** Minutos de intervenir el tablero: desmontar, ordenar, identificar y remontar. */
  minutoTablero: 90,

  // --- Videovigilancia ---
  /** Minutos por metro de UTP: canalizar por plafón o muro, tender y conectorizar. */
  minutoUtp: 9,
  /** Minutos por cámara: montaje, apuntado fino, enfoque y alta en el grabador. */
  minutoCamara: 40,
  /** Minutos de armar el grabador: rack, switch, disco, configuración y pruebas. */
  minutoGrabador: 120,

  // --- Wi-Fi ---
  /** Minutos por punto de acceso: montaje, alimentación, alta y prueba de cobertura. */
  minutoAP: 50,
  /** Minutos de configurar la red: SSID, canales, VLAN, y el recorrido de validación. */
  minutoRed: 150,

  // --- Iluminación ---
  /** Minutos por luminaria: perforar plafón, conectar, montar y nivelar. */
  minutoLuminaria: 25,
  /** Minutos por módulo de control: cablear en la caja, dar de alta y probar. */
  minutoModuloControl: 45,
  /** Minutos de armar escenas, horarios y automatizaciones, y enseñárselas al cliente. */
  minutoEscenas: 120,

  // --- Fabricación en madera ---
  /* OJO: los minutos de TALLER ya están dentro del costo, porque `fabricacion.js` cobra
     el minuto de máquina y el de banco. Estos dos son lo que pasa FUERA del taller, y
     contarlos aquí otra vez inflaría la mano de obra del rubro. */
  /** Minutos por mueble instalado en sitio: nivelar, fijar, ajustar puertas. */
  minutoInstalarMueble: 55,
  /** Minutos de cargar, trasladar y proteger el mueble hasta el sitio. */
  minutoEntrega: 120
}

/**
 * Porción del gasto fijo diario que carga un levantamiento.
 *
 * `factor` es el concepto que ya usaba el motor: si el gasto fijo son 2,800 al día y algo
 * tiene factor 0.40, ese algo absorbe 1,120 de gasto de operación. Un levantamiento ocupa
 * la jornada entera de una cuadrilla, así que su factor se deriva de los **días de obra**
 * que salen de sus propios minutos, en vez de ser un número puesto a mano.
 */
export function factorPorMinutos (minutos, minutosDia = 480) {
  return Math.max(0.1, Math.round((minutos / minutosDia) * 100) / 100)
}

/* ============================================================================
   Riego
   ========================================================================== */

/**
 * Convierte el resultado de `calcular()` de riego en un producto cotizable.
 *
 * Los `comp` salen tal cual de la lista de material, que ya viene resuelta contra el
 * catálogo de Juan. **Los renglones que no resolvieron se descartan de `comp`** —no se
 * puede cobrar lo que no tiene precio— pero se conservan en `pendientes` para que la
 * pantalla los diga. Meterlos con costo cero sería exactamente el error que la lista de
 * material se cuidó de no cometer.
 */
export function riegoAProducto (r, { rendimientos = RENDIMIENTOS_BASE, minutosDia = 480, nombre } = {}) {
  const rd = { ...RENDIMIENTOS_BASE, ...rendimientos }

  const nAspersores = r.materiales
    .filter(m => m.sku && /^ASP-CUERPO$/.test(m.sku))
    .reduce((s, m) => s + m.cantidad, 0)
    || r.cobertura?.aspersores || 0

  const minutos =
    Math.round(r.red.metros) * rd.minutoZanja +
    nAspersores * rd.minutoAspersor +
    r.zonas.length * rd.minutoValvula +
    (r.zonas.length ? rd.minutoProgramador : 0) +
    (r.bomba && r.bomba.bomba ? rd.minutoBomba : 0)

  return armar({
    prefijo: 'RIEGO',
    nombre: nombre || `Riego de jardín — ${Math.round(r.areaJardin)} m²`,
    materiales: r.materiales,
    minutos, minutosDia,
    disenio: {
      tipo: 'riego',
      areaJardin: r.areaJardin,
      cobertura: r.cobertura?.porcentaje,
      zonas: r.zonas.length,
      gastoPico: r.gastoPico,
      diametro: r.diametro?.pulgadas,
      metrosRed: r.red.metros,
      bomba: r.bomba?.bomba?.modelo || null,
      alturaTotal: r.bomba?.alturaTotal || null
    },
    resumen: [
      `${Math.round(r.areaJardin)} m² de jardín`,
      `${nAspersores} aspersores en ${r.zonas.length} ${r.zonas.length === 1 ? 'zona' : 'zonas'}`,
      `${Math.round(r.red.metros)} m de tubería de ${r.diametro?.pulgadas}`,
      r.bomba?.bomba ? `bomba ${r.bomba.bomba.modelo}` : 'sin bomba'
    ]
  })
}

/* ============================================================================
   Eléctrico
   ========================================================================== */

/**
 * Convierte el resultado de `calcularInstalacion()` en un producto cotizable.
 *
 * La canalización se cobra por los metros que salen de la lista de material, no por la
 * suma de los circuitos: varios circuitos comparten tubo y contarlos por separado inflaría
 * la mano de obra del rubro más caro del presupuesto.
 */
export function electricoAProducto (r, { rendimientos = RENDIMIENTOS_BASE, minutosDia = 480, nombre } = {}) {
  const rd = { ...RENDIMIENTOS_BASE, ...rendimientos }

  const metrosTubo = r.materiales
    .filter(m => m.sku && m.sku.startsWith('COND-'))
    .reduce((s, m) => s + m.cantidad, 0)

  const minutos =
    metrosTubo * rd.minutoCanalizacion +
    r.circuitos.length * rd.minutoCircuito +
    rd.minutoTablero

  return armar({
    prefijo: 'ELEC',
    nombre: nombre || `Instalación eléctrica — ${r.m2} m²`,
    materiales: r.materiales,
    minutos, minutosDia,
    disenio: {
      tipo: 'electrico',
      m2: r.m2,
      circuitos: r.circuitos.length,
      demandaVA: r.demandaTotalVA,
      acometidaA: r.corrienteAcometida,
      metodoDemanda: r.demanda?.manda,
      porqueDemanda: r.demanda?.porque,
      desequilibrio: r.balance?.desequilibrio,
      espaciosTablero: r.espaciosTablero
    },
    /* El supuesto viaja CON la partida hasta la cotización. Si se queda solo en la
       pantalla del levantamiento, mañana nadie recuerda que los 6,000 minutos de mano de
       obra salieron de distancias que nunca se midieron. */
    supuestos: r.metros?.circuitosSupuestos
      ? [`${Math.round(r.metros.porcentajeSupuesto * 100)}% de la canalización se calculó con ` +
         `distancias supuestas de ${r.metros.porDefecto} m, no medidas`]
      : [],
    resumen: [
      `${r.circuitos.length} circuitos en ${r.m2} m²`,
      `acometida de ${Math.round(r.corrienteAcometida)} A`,
      `tablero de ${r.espaciosTablero} espacios`,
      `${metrosTubo} m de canalización`
    ]
  })
}

/* ============================================================================
   Videovigilancia
   ========================================================================== */

/**
 * Convierte el resultado de  de cámaras en un producto cotizable.
 *
 * La mano de obra de este rubro la domina el CABLE, no las cámaras: montar y apuntar una
 * cámara son minutos; canalizar ochenta metros de UTP por plafón y muro es la jornada.
 */
export function camarasAProducto (r, { rendimientos = RENDIMIENTOS_BASE, minutosDia = 480, nombre } = {}) {
  const rd = { ...RENDIMIENTOS_BASE, ...rendimientos }

  const minutos =
    r.red.metros * rd.minutoUtp +
    r.camaras.length * rd.minutoCamara +
    rd.minutoGrabador

  return armar({
    prefijo: 'CCTV',
    nombre: nombre || `Videovigilancia — ${r.camaras.length} cámaras`,
    materiales: r.materiales,
    minutos, minutosDia,
    disenio: {
      tipo: 'camaras',
      areaRecinto: r.areaRecinto,
      nivelObjetivo: r.nivelObjetivo,
      porNivel: r.porNivel.map(n => ({ id: n.id, nombre: n.nombre, porcentaje: n.porcentaje })),
      camaras: r.camaras.map(c => ({ modelo: c.ficha.modelo, lente: c.ficha.lente, rumbo: c.rumbo })),
      metrosUtp: r.red.metros,
      watts: r.poe.consumo,
      disco: r.disco.tbComercial,
      dias: r.disco.dias
    },
    /* El tramo fuera de límite NO es un supuesto: es un hecho geométrico que obliga a
       cotizar aparte. Va como supuesto igual porque es lo que puede cambiar el precio
       después de firmar, que es la razón por la que este campo existe. */
    supuestos: r.red.fueraDeLimite
      ? [`${r.red.fueraDeLimite} tramo(s) de cable pasan de los 100 m que admite el cobre: ` +
         'piden switch intermedio o fibra, y eso NO está cotizado aquí']
      : [],
    resumen: [
      `${r.camaras.length} cámaras sobre ${Math.round(r.areaRecinto)} m²`,
      `${r.porNivel.find(n => n.id === r.nivelObjetivo)?.porcentaje.toFixed(0)}% a nivel ` +
        `${r.porNivel.find(n => n.id === r.nivelObjetivo)?.nombre.toLowerCase()}`,
      `${r.red.metros} m de UTP`,
      `${r.disco.tbComercial} TB para ${r.disco.dias} días`
    ]
  })
}

/* ============================================================================
   Wi-Fi
   ========================================================================== */

/**
 * Convierte el resultado de `calcular()` de Wi-Fi en un producto cotizable.
 *
 * **El supuesto que viaja aquí es distinto a todos los demás, y es el más importante del
 * archivo:** los otros rubros estiman una cantidad; éste estima una *cobertura*, y la
 * documentación del propio fabricante dice que eso se valida midiendo. Si esa advertencia
 * se queda en la pantalla del levantamiento y no llega a la cotización, el cliente firma
 * una promesa que nadie hizo.
 */
export function wifiAProducto (r, { rendimientos = RENDIMIENTOS_BASE, minutosDia = 480, nombre } = {}) {
  const rd = { ...RENDIMIENTOS_BASE, ...rendimientos }

  const minutos =
    r.red.metros * rd.minutoUtp +
    r.colocados * rd.minutoAP +
    rd.minutoRed

  const supuestos = [
    'La cobertura es una ESTIMACIÓN, no una medición: se valida con un recorrido el día ' +
    'de la instalación y puede obligar a mover un equipo'
  ]
  if (r.faltan > 0) {
    supuestos.push(`Se colocaron ${r.colocados} equipos en el plano y el cálculo pide ` +
                   `${r.recomendacion.cantidad}: faltan ${r.faltan} por ubicar`)
  }
  if (!r.snr.sirveVoz) {
    supuestos.push(`El SNR medido (${r.snr.valor} dB) no llega al mínimo de voz: hay ` +
                   'interferencia y puede hacer falta trabajo de canales, no más equipos')
  }

  return armar({
    prefijo: 'WIFI',
    nombre: nombre || `Wi-Fi — ${r.colocados} ${r.colocados === 1 ? 'punto' : 'puntos'} de acceso`,
    materiales: r.materiales,
    minutos, minutosDia,
    disenio: {
      tipo: 'wifi',
      areaPlanta: r.areaPlanta,
      banda: r.banda.nombre,
      colocados: r.colocados,
      recomendados: r.recomendacion.cantidad,
      manda: r.recomendacion.manda,
      alcanceEstimado: r.recomendacion.alcanceEstimado,
      cobertura: r.cobertura.porcentaje,
      snr: r.snr.valor,
      pisoRuido: r.snr.pisoRuido,
      metrosUtp: r.red.metros
    },
    supuestos,
    resumen: [
      `${r.colocados} de ${r.recomendacion.cantidad} puntos sobre ${Math.round(r.areaPlanta)} m²`,
      `manda ${r.recomendacion.manda}`,
      `${r.cobertura.porcentaje.toFixed(0)}% estimado en ${r.banda.nombre}`,
      `SNR ${r.snr.valor} dB`
    ]
  })
}

/* ============================================================================
   Iluminación
   ========================================================================== */

/**
 * Convierte el resultado de `calcular()` de iluminación en un producto cotizable.
 *
 * **El supuesto que viaja aquí es de responsabilidad, no de cantidad.** En los otros
 * rubros el supuesto dice «este número puede moverse». Aquí dice **qué se le puede
 * prometer al cliente**: si un espacio no es centro de trabajo, decir que «cumple norma»
 * es prometer algo que no existe, y esa frase tiene que llegar hasta el documento.
 */
export function iluminacionAProducto (r, { rendimientos = RENDIMIENTOS_BASE, minutosDia = 480, nombre } = {}) {
  const rd = { ...RENDIMIENTOS_BASE, ...rendimientos }

  const modulos = Object.values(r.porControl).reduce((a, b) => a + b, 0)
  const minutos =
    r.totalLuminarias * rd.minutoLuminaria +
    modulos * rd.minutoModuloControl +
    (modulos ? rd.minutoEscenas : 0)

  const supuestos = []
  if (r.espaciosConNorma < r.detalle.length) {
    supuestos.push(
      `${r.detalle.length - r.espaciosConNorma} de ${r.detalle.length} espacios NO son ` +
      'centro de trabajo: ahí el nivel es recomendación de confort, no cumplimiento de ' +
      'norma. No existe norma de iluminación para vivienda'
    )
  }
  supuestos.push(
    'La cantidad de luminarias usa un coeficiente de utilización de trabajo, no la tabla ' +
    'fotométrica de la luminaria elegida: es una propuesta correcta en orden de magnitud, ' +
    'no un cálculo luminotécnico certificado'
  )
  if (r.detalle.some(d => d.avisoAtenuacion)) {
    supuestos.push(
      'Los espacios atenuados exigen lámparas atenuables y una carga mínima que depende ' +
      'del módulo: verificar en la ficha antes de comprometerlo'
    )
  }

  return armar({
    prefijo: 'ILUM',
    nombre: nombre || `Iluminación — ${r.detalle.length} espacios`,
    materiales: r.materiales,
    minutos, minutosDia,
    disenio: {
      tipo: 'iluminacion',
      espacios: r.detalle.map(d => ({
        nombre: d.espacio.nombre,
        m2: d.areaM2,
        lux: d.lux,
        luxReales: d.luminarias.luxReales,
        luminarias: d.luminarias.cantidad,
        control: d.control.nombre,
        regimen: d.regimen,
        exigible: d.exigible?.lux || null,
        rcr: d.rcr
      })),
      totalArea: r.totalArea,
      totalLuminarias: r.totalLuminarias,
      espaciosConNorma: r.espaciosConNorma
    },
    supuestos,
    resumen: [
      `${r.detalle.length} espacios · ${Math.round(r.totalArea)} m²`,
      `${r.totalLuminarias} luminarias`,
      modulos ? `${modulos} módulos de control` : 'control convencional',
      r.espaciosConNorma
        ? `${r.espaciosConNorma} con nivel exigible por norma`
        : 'ninguno cae bajo la NOM-025'
    ]
  })
}

/* ============================================================================
   Fabricación en madera
   ========================================================================== */

/**
 * Convierte el resultado de `calcularTaller()` en un producto cotizable.
 *
 * **Este rubro no pasa por el catálogo de insumos, y es la única excepción del sistema.**
 * Los otros cinco resuelven su material contra lo que Juan tenga dado de alta; aquí el
 * costo sale del despiece —placas enteras, minutos de máquina, herrajes— que `taller.js`
 * ya calculó contra la configuración del taller. Meterlo otra vez por el catálogo lo
 * contaría dos veces.
 *
 * Por eso el producto lleva `precio` con el costo ya resuelto en vez de `comp`: es la
 * forma que el motor de precios usa para un concepto que trae su costo hecho.
 */
export function tallerAProducto (r, { rendimientos = RENDIMIENTOS_BASE, minutosDia = 480, nombre, factores = {} } = {}) {
  const rd = { ...RENDIMIENTOS_BASE, ...rendimientos }

  /* Los minutos de taller ya están dentro del costo —el motor de fabricación cobra el
     minuto de máquina y el de banco—. Lo que falta aquí es lo que pasa FUERA del taller:
     medir, entregar e instalar. Contar los de taller otra vez inflaría la mano de obra. */
  const minutos = r.detalle.reduce((s, d) => s + d.cantidad, 0) * rd.minutoInstalarMueble +
                  rd.minutoEntrega

  const supuestos = []
  if (!r.hueco.hayHueco) {
    supuestos.push('No se capturó la medida del hueco: nadie comprobó que los muebles entren')
  } else {
    if (!r.hueco.cabe) {
      supuestos.push(`Los muebles NO entran en el hueco medido: sobran ` +
                     `${Math.abs(Math.round(r.hueco.sobra))} mm. Hay que rediseñar antes de cortar`)
    }
    if (r.hueco.unaSolaMedida) {
      supuestos.push('El hueco se midió en un solo punto, así que no se sabe si el muro ' +
                     'está derecho. Conviene medirlo arriba, a la mitad y abajo antes de cortar')
    } else if (r.hueco.derecho === false) {
      supuestos.push(`El muro varía ${r.hueco.desplome} mm: hace falta un suplemento de ` +
                     'ajuste que NO está cotizado aquí')
    }
  }
  supuestos.push('El precio por metro NO incluye instalación, cubierta, acabados especiales ' +
                 'ni herrajes premium. Ver la lista completa en el levantamiento')
  if (r.sinVerificar) {
    supuestos.push('Los costos del taller —placa, minuto de máquina, herraje— todavía no ' +
                   'salen de la operación real: se calibran en Configuración → Taller')
  }

  /* EL COSTO DE FABRICACIÓN ENTRA POR `precio`, NO POR `comp`, y hay que explicarlo.

     Los otros cinco rubros dejan que el motor de precios aplique el margen a cada
     componente. Aquí el costo ya viene resuelto del despiece, así que se le aplica el
     mismo trato que a un complemento —margen por IVA— y entra como precio hecho.

     Se usa `UtilidadComplementos` y no un margen propio del taller porque una placa
     cortada es exactamente eso: un complemento que se compra, se transforma y se entrega.
     Si Juan decide que la fabricación lleva otro margen, ése es un factor nuevo y va en
     Configuración → Factores, no escondido aquí. */
  const f = { UtilidadComplementos: 1.45, IVA: 1.16, TDC: 1, ...factores }
  const precio = Math.ceil(r.costoTotal * f.UtilidadComplementos * f.IVA * f.TDC)

  return armar({
    prefijo: 'MAD',
    nombre: nombre || `Fabricación — ${r.metrosTotal.toFixed(2)} m de frente`,
    /* Sin materiales del catálogo: el costo ya viene resuelto del despiece, y meterlo
       otra vez por el catálogo lo contaría dos veces. Es la única excepción del sistema. */
    materiales: [],
    precio,
    minutos, minutosDia,
    disenio: {
      tipo: 'taller',
      metrosFrente: r.metrosTotal,
      costoFabricacion: r.costoTotal,
      costoPorMetro: r.costoPorMetro,
      modulos: r.detalle.map(d => ({
        nombre: d.modulo.nombre,
        ancho: d.medidas.ancho, alto: d.medidas.alto, fondo: d.medidas.fondo,
        cantidad: d.cantidad,
        costo: d.costo,
        minutosMaquina: d.fab.minutosMaquina
      })),
      placas: r.placas,
      hueco: r.hueco.hayHueco
        ? { menor: r.hueco.menor, mayor: r.hueco.mayor, desplome: r.hueco.desplome, cabe: r.hueco.cabe }
        : null
    },
    supuestos,
    resumen: [
      `${r.detalle.reduce((s, d) => s + d.cantidad, 0)} módulos · ${r.metrosTotal.toFixed(2)} m de frente`,
      `${Math.round(r.costoPorMetro).toLocaleString('es-MX')} por metro, de costo`,
      Object.entries(r.placas).map(([, p]) => `${p.placas} ${p.nombre}`).join(' · '),
      r.hueco.hayHueco ? (r.hueco.cabe ? 'entra en el hueco medido' : 'NO entra en el hueco') : 'hueco sin medir'
    ]
  })
}

/* ============================================================================
   Lo común
   ========================================================================== */

function armar ({ prefijo, nombre, materiales, minutos, minutosDia, disenio, resumen, supuestos = [], precio = 0 }) {
  const cobrables = materiales.filter(m => !m.falta && m.sku)
  const pendientes = materiales.filter(m => m.falta)

  /* Los `comp` se agrupan por SKU. La lista de material ya sale agrupada, pero un
     levantamiento puede repetir un SKU —dos modelos de boquilla que resuelven al mismo
     insumo, por ejemplo— y el motor multiplica `c` por la cantidad del renglón: dos
     renglones del mismo SKU se cobrarían por separado y el total saldría bien, pero la
     lista impresa mostraría el mismo concepto dos veces. */
  const porSku = {}
  for (const m of cobrables) porSku[m.sku] = (porSku[m.sku] || 0) + m.cantidad

  return {
    sku: `${prefijo}-${Date.now().toString(36)}${Math.floor(Math.random() * 1e4).toString(36)}`,
    nombre,
    cat: 'dia',
    tIns: minutos,
    tCfg: 0,
    /* `precio: 0` significa «se calcula». El precio sale de los componentes más la mano
       de obra más los márgenes que Juan tenga puestos, que es justo lo que hace falta:
       un levantamiento no tiene precio de lista. */
    precio,
    factor: factorPorMinutos(minutos, minutosDia),
    comp: Object.entries(porSku).map(([sku, c]) => ({ sku, c })),
    especial: true,
    /** El levantamiento entero, para poder auditarlo meses después. */
    disenio,
    /** Frases cortas para la tarjeta del renglón, sin volver a derivar nada. */
    resumen,
    /** Lo que se calculó sobre un supuesto y no sobre una medición. */
    supuestos,
    /** Lo que quedó sin cobrar por no estar en el catálogo. Se dice, no se esconde. */
    pendientes: pendientes.map(m => ({
      concepto: m.concepto.replace('Falta dar de alta: ', ''),
      cantidad: m.cantidad, unidad: m.unidad
    }))
  }
}
